import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, Plus, Trash2, ArrowRight } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { useSaved } from '../lib/SavedContext';
import { useCart } from '../lib/cartContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WishlistPage() {
  const { user, token, loading: authLoading } = useAuth();
  const { toggleSaved } = useSaved();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingId, setAddingId] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) { navigate('/auth'); return; }
    if (!token) return;
    fetch(`${API}/api/saved/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setItems((Array.isArray(d) ? d : []).filter(i => i.type === 'product')))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [token, user, authLoading, navigate]);

  const handleRemove = async (item) => {
    await toggleSaved({ type: 'product', item_id: item.item_id });
    setItems(prev => prev.filter(i => i.item_id !== item.item_id));
  };

  const handleAddToCart = async (item) => {
    setAddingId(item.item_id);
    await addItem(item.item_id);
    setAddingId(null);
  };

  if (authLoading || loading) {
    return (
      <main className="pt-20 min-h-screen" data-testid="wishlist-page">
        <div className="container-ninja section-spacing text-center">
          <div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen" data-testid="wishlist-page">
      <section className="container-ninja section-spacing">
        <div className="max-w-3xl">
          <p className="text-ninja-label text-[#E63946] mb-4 flex items-center gap-2">
            <Heart className="w-4 h-4" /> My Wishlist
          </p>
          <h1 className="text-white mb-2">
            Saved <span className="text-gradient-ninja">Products</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            {items.length} item{items.length !== 1 ? 's' : ''} saved for later
          </p>
        </div>
      </section>

      <section className="container-ninja pb-24">
        {items.length === 0 ? (
          <div className="text-center py-20 bg-zinc-900/50 border border-zinc-800" data-testid="wishlist-empty">
            <Heart className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 text-sm mb-2">Your wishlist is empty</p>
            <p className="text-zinc-600 text-xs mb-6">Browse our shop and save products you love</p>
            <Link to="/shop" className="btn-ninja inline-flex items-center gap-2 px-6 py-3 text-sm" data-testid="wishlist-browse-btn">
              <ShoppingBag className="w-4 h-4" /> Browse Shop
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map(item => (
              <div key={item.item_id} className="group bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 overflow-hidden" data-testid={`wishlist-item-${item.item_id}`}>
                <Link to={item.slug ? `/shop/${item.slug}` : '#'} className="block">
                  <div className="relative aspect-square overflow-hidden bg-zinc-900">
                    {item.image ? (
                      <img src={item.image} alt={item.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-12 h-12 text-zinc-700" />
                      </div>
                    )}
                  </div>
                </Link>

                <div className="p-4">
                  {item.category && (
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{item.category}</span>
                  )}
                  <Link to={item.slug ? `/shop/${item.slug}` : '#'}>
                    <h3 className="text-white text-sm font-heading mt-1 group-hover:text-[#E63946] transition-colors line-clamp-2" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1rem' }}>
                      {item.title}
                    </h3>
                  </Link>
                  {item.price && (
                    <p className="text-[#E63946] text-lg font-semibold mt-2">{typeof item.price === 'number' ? `$${item.price.toFixed(2)}` : item.price}</p>
                  )}

                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleAddToCart(item)}
                      disabled={addingId === item.item_id}
                      className="flex-1 btn-ninja py-2.5 text-xs flex items-center justify-center gap-1.5"
                      data-testid={`wishlist-add-cart-${item.item_id}`}
                    >
                      {addingId === item.item_id ? (
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-3.5 h-3.5" /> Add to Cart
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleRemove(item)}
                      className="w-10 h-10 flex items-center justify-center border border-zinc-700 text-zinc-500 hover:text-red-400 hover:border-red-400/40 transition-colors"
                      data-testid={`wishlist-remove-${item.item_id}`}
                      aria-label="Remove from wishlist"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="text-center mt-12">
            <Link to="/shop" className="inline-flex items-center gap-2 text-zinc-400 text-sm hover:text-white transition-colors" data-testid="wishlist-continue-shopping">
              Continue Shopping <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}
